/**
 * share.ts â€” private share links for finished debates.
 *
 * A link points at `sharedDebates/{shareId}`, a copy made by the
 * `createShareLink` Cloud Function. Anyone with the id can read it; it is
 * never listed or shown on the public feed.
 */

import { doc, getDoc, Timestamp } from "firebase/firestore";
import { db } from "./config";
import { callFeedFunction } from "./feed";
import { Verdict, parseVerdict } from "../utils/verdict";
import { DebateSource, parseSources } from "../utils/sources";

export interface SharedDebate {
  shareId: string;
  question: string;
  experts: string[];
  debateMode: "single" | "multi";
  authorName: string;
  steps: any[];
  chaosSteps: any[];
  finalAggregate: string;
  verdict: Verdict | null;
  sources?: DebateSource[];
  sharedAt: Timestamp | null;
}

export function shareUrl(shareId: string): string {
  return `${window.location.origin}/d/${shareId}`;
}

export async function createShareLink(debateId: string, anonymous: boolean): Promise<string> {
  return (await callFeedFunction("createShareLink", { debateId, anonymous })).shareId;
}

export async function revokeShareLink(debateId: string): Promise<void> {
  await callFeedFunction("revokeShareLink", { debateId });
}

/** The live share id for the owner's debate, if it has one. */
export async function currentShareId(debateId: string): Promise<string | null> {
  try {
    const id = (await getDoc(doc(db, "debates", debateId))).get("shareId");
    return typeof id === "string" ? id : null;
  } catch {
    return null;
  }
}

/** Resolves null when the link was turned off or never existed. */
export async function loadSharedDebate(shareId: string): Promise<SharedDebate | null> {
  try {
    const snap = await getDoc(doc(db, "sharedDebates", shareId));
    if (!snap.exists()) return null;
    const d = snap.data();
    return {
      shareId,
      question: d.question ?? "",
      experts: d.experts ?? [],
      debateMode: d.debateMode === "single" ? "single" : "multi",
      authorName: d.authorName ?? "Anonymous",
      steps: d.steps ?? [],
      chaosSteps: d.chaosSteps ?? [],
      finalAggregate: d.finalAggregate ?? "",
      verdict: parseVerdict(d.verdict),
      sources: parseSources(d.sources),
      sharedAt: d.sharedAt ?? null,
    };
  } catch (err: any) {
    if (err?.code === "permission-denied") return null;
    throw err;
  }
}
